import {TemplateInstance, Parameters, MaterialReference} from "@cm/template-nodes"
import type {Camera, IModifyTemplateGraph, ParametersParameters} from "@cm/template-nodes"
import type {MattressFabricLayout} from "#mattress/nodes/mattress-cut"
import {
    createMattress,
    createMattressMesh,
    createMattressGroup,
    createMattressReferenceFrame,
    createMattressComponentGraph,
    createMattressComponentInstance,
    createMattressCut,
} from "#mattress/assets-definitions/create-mattress"
import {createMathOperator, createTemplateReference, createNumberInput, createMaterialInput} from "#mattress/assets-definitions/common"
import {getComponentGraphNodesForStackableStandardModelDimensionsLogic} from "#mattress/assets-definitions/standard-models/common/dimension-logic"
import {
    getComponentGraphNodesForBottomPart,
    getComponentGraphNodesForMiddlePart,
    getComponentGraphNodesForTopPart,
    getComponentGraphNodesForBottomPanel,
} from "#mattress/assets-definitions/standard-models/common/parts"
import {getComponentGraphNodesForHandles} from "#mattress/assets-definitions/standard-models/common/handles"
import type {MattressUVControlPoint} from "#mattress/nodes/curve/mattress-curve"
import {
    numberInputMetaData,
    uiPathMetaData,
    createPath,
    dimensionPathName,
    panelPathName,
    bottomPanelPathName,
    topPanelPathName,
} from "#mattress/mattress-meta-data"
import {MattressCurve} from "#mattress/nodes/curve/mattress-curve"
import {getComponentGraphForAccessoryStack} from "#mattress/assets-definitions/standard-models/common/accessory-stack"
import {
    getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate,
    getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate,
} from "#mattress/assets-definitions/standard-models/common/material-offset"

import {Matrix4} from "@cm/math"

export type InitStackableMattressStandardModelMasterTemplateOptions = {
    openingOnBottom: boolean
    topPartDefaultMaterialLegacyId?: number
    bottomPartDefaultMaterialLegacyId?: number
    middlePartDefaultMaterialLegacyId?: number
    defaultLayerHeight?: number
    defaultLayerCount?: number
    topPartFabricLayout?: MattressFabricLayout
    bottomPartFabricLayout?: MattressFabricLayout
    middlePartFabricLayout?: MattressFabricLayout
    bottomPanelMode?: "edge" | "mirror"
}

export function initRootComponentGraphForStackableMattressStandardModel(
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
) {
    const {
        openingOnBottom,
        topPartDefaultMaterialLegacyId,
        bottomPartDefaultMaterialLegacyId,
        middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        topPartFabricLayout,
        bottomPartFabricLayout,
        middlePartFabricLayout,
        bottomPanelMode,
    } = options

    // dimensions
    const {width, length, cornerRadius, topRadius, bottomRadius, bottomHeight, topHeight, safeBottomRadius, safeTopRadius} =
        getComponentGraphNodesForStackableStandardModelDimensionsLogic()

    // bottom part
    const {bottomHeightCurve, bottomPartGroup, topAndBottomPartMaterialOffsetComponentGraph} = getComponentGraphNodesForBottomPart({
        bottomHeight,
        width,
        length,
        safeBottomRadius,
        openingOnBottom,
        defaultMaterialLegacyId: bottomPartDefaultMaterialLegacyId,
        fabricLayout: bottomPartFabricLayout,
        bottomPanelMode,
    })

    // middle part / stack
    const {middlePartGroup, middlePartTopOffset, middlePartTopCurve, middlePartTopLayerMiddleCurve, middlePartFabricLayersCount} =
        getComponentGraphNodesForMiddlePart({
            bottomHeight,
            bottomHeightCurve,
            defaultMaterialLegacyId: middlePartDefaultMaterialLegacyId,
            defaultLayerHeight,
            defaultLayerCount,
            fabricLayout: middlePartFabricLayout,
        })

    // top part
    const {topPartGroup} = getComponentGraphNodesForTopPart({
        topHeight,
        width,
        length,
        safeTopRadius,
        middlePartTopCurve,
        defaultMaterialLegacyId: topPartDefaultMaterialLegacyId,
        fabricLayout: topPartFabricLayout,
        topAndBottomPartMaterialOffsetComponentGraph,
    })

    const height = createMathOperator("Mattress Height", "add", middlePartTopOffset, topHeight)
    const sumSafeTopRadiusSafeBottomRadius = createMathOperator("Safe Top Radius + Safe Bottom Radius", "add", safeTopRadius, safeBottomRadius)
    const safeHeight = createMathOperator("Safe Height", "max", sumSafeTopRadiusSafeBottomRadius, height)

    const dimensionsGroup = createMattressGroup("Dimensions", [
        width,
        length,
        cornerRadius,
        topRadius,
        bottomRadius,
        bottomHeight,
        topHeight,
        safeBottomRadius,
        safeTopRadius,
        height,
        sumSafeTopRadiusSafeBottomRadius,
        safeHeight,
    ])

    const mattressMesh = createMattressMesh("Mattress Mesh", {
        width,
        length,
        height: safeHeight,
        cornerRadius,
        topRadius: safeTopRadius,
        bottomRadius: safeBottomRadius,
    })

    // handles
    const {nodes: handlesNodes} = getComponentGraphNodesForHandles({
        bottomHeightCurve,
        middlePartTopCurve,
        middlePartTopLayerMiddleCurve,
        mattressWidth: width,
        middlePartFabricLayersCount,
    })
    const handlesGroup = createMattressGroup("Handles", handlesNodes)

    return {
        mattressMesh,
        dimensionsGroup,
        topPartGroup,
        middlePartGroup,
        bottomPartGroup,
        handlesGroup,
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////

const kEps = 0.2 // cm

// BS006A
export function initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS006A(
    options: InitStackableMattressStandardModelMasterTemplateOptions,
    curveParams: {
        patternVerticalSize: number
        controlPointsData: Omit<MattressUVControlPoint, "uvSpace">[]
    },
) {
    const {
        openingOnBottom,
        topPartDefaultMaterialLegacyId,
        bottomPartDefaultMaterialLegacyId,
        middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        topPartFabricLayout,
        bottomPartFabricLayout,
        middlePartFabricLayout,
    } = options

    // dimensions
    const patternVerticalSize = createNumberInput(
        "Pattern Vertical Size",
        "pattern-vertical-size",
        curveParams.patternVerticalSize,
        numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 7),
    )
    const halfPatternVerticalSize = createMathOperator("Half Pattern Vertical Size", "mul", patternVerticalSize, 0.5)

    const width = createNumberInput("Width", "width", 90, numberInputMetaData([createPath(dimensionPathName, 0)], 50, 300, 0))
    const length = createNumberInput("Length", "length", 200, numberInputMetaData([createPath(dimensionPathName, 0)], 50, 300, 1))
    const cornerRadius = createNumberInput("Corner Radius", "corner-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 6))
    const topRadius = createNumberInput("Top Radius", "top-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 4))
    const bottomRadius = createNumberInput("Bottom Radius", "bottom-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 5))
    const bottomHeight = createNumberInput(
        "Bottom Height",
        "bottom-height",
        16,
        numberInputMetaData([createPath(dimensionPathName, 0), createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 1, 100, 3),
    )
    const topHeight = createNumberInput(
        "Top Height",
        "top-height",
        8,
        numberInputMetaData([createPath(dimensionPathName, 0), createPath(panelPathName, 0, createPath(topPanelPathName, 0))], 1, 100, 2),
    )

    const safeBottomRadius = createMathOperator("Safe Bottom Radius", "min", bottomRadius, cornerRadius)
    const safeTopRadius = createMathOperator("Safe Top Radius", "min", topRadius, cornerRadius)

    const minAllowedBottomHeight = createMathOperator("Min Allowed Bottom Height", "add", patternVerticalSize, kEps)
    const safeBottomHeight = createMathOperator("Safe Bottom Height", "max", minAllowedBottomHeight, bottomHeight)
    const sumSafeTopRadiusSafeBottomRadius = createMathOperator("Safe Top Radius + Safe Bottom Radius", "add", safeTopRadius, safeBottomRadius)

    // ref curve
    const refCurveOffsetNoEps = createMathOperator("Ref Curve Offset No Eps", "sub", safeBottomHeight, halfPatternVerticalSize)
    const refCurveOffset = createMathOperator("Ref Curve Offset", "add", refCurveOffsetNoEps, 0.001)
    const refReferenceFrame = createMattressReferenceFrame("Ref Reference Frame", "zAxisRing", "global", refCurveOffset, halfPatternVerticalSize)
    const refCurve = new MattressCurve({
        parameters: {
            name: "Ref Curve",
            closed: true,
            resolution: 1,
            controlPoints: curveParams.controlPointsData.map((pointData) => ({
                ...pointData,
                uvSpace: refReferenceFrame,
            })),
        },
    })
    const refCurveGroup = createMattressGroup("Ref Curve", [refReferenceFrame, refCurve, refCurveOffsetNoEps, refCurveOffset])

    // bottom part
    const {nodes: accessoriesNodes} = getComponentGraphForAccessoryStack(1)
    const accessoriesGraph = createMattressComponentGraph("Accessories", accessoriesNodes)
    const bottomCurveAccessories = createMattressComponentInstance("Accessories Bottom Curve", "accessories-bottom-curve", accessoriesGraph, {
        "ref-curve": refCurve,
    })
    const bottomPartMaterial = createMaterialInput(
        "Bottom Part Material",
        "bottom-part-material",
        uiPathMetaData([createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 0),
        bottomPartDefaultMaterialLegacyId
            ? new MaterialReference({
                  parameters: {
                      name: "Default Bottom Part Material",
                      materialId: bottomPartDefaultMaterialLegacyId,
                      materialRevisionId: null,
                  },
              })
            : undefined,
    )
    const topAndBottomPartMaterialOffsetComponentGraph = getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate()
    const {materialOffsetComponentInstance: bottomPartMaterialOffsetInstance, materialOffsetOutput: bottomPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Bottom Part Material Offset",
            componentInstanceId: "bottom-part-material-offset",
            outputName: "Bottom Part Material Offset",
            material: bottomPartMaterial,
            width: width,
            length: length,
            heigth: safeBottomHeight,
            edgeRadius: safeBottomRadius,
        })

    const bottomPartCut = createMattressCut(
        openingOnBottom ? "Border" : "Bottom Panel",
        [{curve: refCurve, side: "right"}],
        bottomPartFabricLayout,
        bottomPartMaterialOffset,
    )

    const {nodes: bottomPanelNodes} = getComponentGraphNodesForBottomPanel({
        defaultBottomPanelMaterialLegacyId: bottomPartDefaultMaterialLegacyId,
        mode: "mirror",
        width,
        length,
        topAndBottomPartMaterialOffsetComponentGraph,
    })
    const bottomPanelGroup = createMattressGroup("Bottom Panel", bottomPanelNodes)

    const bottomPartGroup = createMattressGroup("Bottom Part", [
        bottomPartMaterial,
        topAndBottomPartMaterialOffsetComponentGraph,
        bottomPartMaterialOffsetInstance,
        bottomPartMaterialOffset,
        bottomPartCut,
        bottomCurveAccessories,
        accessoriesGraph,
        bottomPanelGroup,
    ])

    // middle part / stack
    const {middlePartGroup, middlePartTopOffset, middlePartTopCurve} = getComponentGraphNodesForMiddlePart({
        bottomHeight: refCurveOffset,
        bottomHeightCurve: refCurve,
        layerReferenceFrameUnit: halfPatternVerticalSize,
        defaultMaterialLegacyId: middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        fabricLayout: middlePartFabricLayout,
    })

    // top part
    const sumTopHeightPatternVerticalSize = createMathOperator("Sum Top Height Pattern Vertical Size", "add", topHeight, patternVerticalSize)
    const {topPartGroup} = getComponentGraphNodesForTopPart({
        topHeight: sumTopHeightPatternVerticalSize,
        width,
        length,
        safeTopRadius,
        middlePartTopCurve: middlePartTopCurve,
        defaultMaterialLegacyId: topPartDefaultMaterialLegacyId,
        fabricLayout: topPartFabricLayout,
        topAndBottomPartMaterialOffsetComponentGraph,
    })
    topPartGroup.parameters.nodes.addEntry(sumTopHeightPatternVerticalSize)

    const middlePartHeight = createMathOperator("Middle Part Height", "add", middlePartTopOffset, halfPatternVerticalSize)
    const height = createMathOperator("Mattress Height", "add", middlePartHeight, topHeight)
    const safeHeight = createMathOperator("Safe Height", "max", sumSafeTopRadiusSafeBottomRadius, height)
    const dimensionsGroup = createMattressGroup("Dimensions", [
        patternVerticalSize,
        halfPatternVerticalSize,
        width,
        length,
        cornerRadius,
        topRadius,
        bottomRadius,
        bottomHeight,
        topHeight,
        safeBottomHeight,
        minAllowedBottomHeight,
        safeBottomRadius,
        safeTopRadius,
        sumSafeTopRadiusSafeBottomRadius,
        middlePartHeight,
        height,
        safeHeight,
    ])

    const mattressMesh = createMattressMesh("Mattress Mesh", {
        width,
        length,
        height: safeHeight,
        cornerRadius,
        topRadius: safeTopRadius,
        bottomRadius: safeBottomRadius,
    })

    return {
        mattressMesh,
        refCurveGroup,
        dimensionsGroup,
        topPartGroup,
        middlePartGroup,
        bottomPartGroup,
    }
}

// BS012A
export function initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS012A(
    options: InitStackableMattressStandardModelMasterTemplateOptions,
    curveParams: {
        patternVerticalSize: number
        controlPointsData: Omit<MattressUVControlPoint, "uvSpace">[]
    },
) {
    const {
        openingOnBottom,
        topPartDefaultMaterialLegacyId,
        bottomPartDefaultMaterialLegacyId,
        middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        topPartFabricLayout,
        bottomPartFabricLayout,
        middlePartFabricLayout,
    } = options

    // dimensions
    const patternVerticalSize = createNumberInput(
        "Pattern Vertical Size",
        "pattern-vertical-size",
        curveParams.patternVerticalSize,
        numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 7),
    )
    const halfPatternVerticalSize = createMathOperator("Half Pattern Vertical Size", "mul", patternVerticalSize, 0.5)

    const width = createNumberInput("Width", "width", 90, numberInputMetaData([createPath(dimensionPathName, 0)], 50, 300, 0))
    const length = createNumberInput("Length", "length", 200, numberInputMetaData([createPath(dimensionPathName, 0)], 50, 300, 1))
    const cornerRadius = createNumberInput("Corner Radius", "corner-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 6))
    const topRadius = createNumberInput("Top Radius", "top-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 4))
    const bottomRadius = createNumberInput("Bottom Radius", "bottom-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 5))
    const bottomHeight = createNumberInput(
        "Bottom Height",
        "bottom-height",
        16,
        numberInputMetaData([createPath(dimensionPathName, 0), createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 1, 100, 3),
    )
    const topHeight = createNumberInput(
        "Top Height",
        "top-height",
        8,
        numberInputMetaData([createPath(dimensionPathName, 0), createPath(panelPathName, 0, createPath(topPanelPathName, 0))], 1, 100, 2),
    )

    const safeBottomRadius = createMathOperator("Safe Bottom Radius", "min", bottomRadius, cornerRadius)
    const safeTopRadius = createMathOperator("Safe Top Radius", "min", topRadius, cornerRadius)

    const minAllowedBottomHeight = createMathOperator("Min Allowed Bottom Height", "add", halfPatternVerticalSize, kEps)
    const safeBottomHeight = createMathOperator("Safe Bottom Height", "max", minAllowedBottomHeight, bottomHeight)

    const minAllowedTopHeight = createMathOperator("Min Allowed Top Height", "add", halfPatternVerticalSize, kEps)
    const safeTopHeight = createMathOperator("Safe Top Height", "max", minAllowedTopHeight, topHeight)

    const sumSafeTopRadiusSafeBottomRadius = createMathOperator("Safe Top Radius + Safe Bottom Radius", "add", safeTopRadius, safeBottomRadius)

    // ref curve
    const refCurveOffset = createMathOperator("Ref Curve Offset", "add", safeBottomHeight, 0.001)
    const refReferenceFrame = createMattressReferenceFrame("Ref Reference Frame", "zAxisRing", "global", refCurveOffset, halfPatternVerticalSize)
    const refCurve = new MattressCurve({
        parameters: {
            name: "Ref Curve",
            closed: true,
            resolution: 1,
            controlPoints: curveParams.controlPointsData.map((pointData) => ({
                ...pointData,
                uvSpace: refReferenceFrame,
            })),
        },
    })
    const refCurveGroup = createMattressGroup("Ref Curve", [refReferenceFrame, refCurve, refCurveOffset])

    // bottom part
    const {nodes: accessoriesNodes} = getComponentGraphForAccessoryStack(1)
    const accessoriesGraph = createMattressComponentGraph("Accessories", accessoriesNodes)
    const bottomCurveAccessories = createMattressComponentInstance("Accessories Bottom Curve", "accessories-bottom-curve", accessoriesGraph, {
        "ref-curve": refCurve,
    })
    const bottomPartMaterial = createMaterialInput(
        "Bottom Part Material",
        "bottom-part-material",
        uiPathMetaData([createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 0),
        bottomPartDefaultMaterialLegacyId
            ? new MaterialReference({
                  parameters: {
                      name: "Default Bottom Part Material",
                      materialId: bottomPartDefaultMaterialLegacyId,
                      materialRevisionId: null,
                  },
              })
            : undefined,
    )

    const sumSafeBottomHeightHalfPatternVerticalSize = createMathOperator(
        "Sum Safe Bottom Height Half Pattern Vertical Size",
        "add",
        safeBottomHeight,
        halfPatternVerticalSize,
    )
    const topAndBottomPartMaterialOffsetComponentGraph = getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate()
    const {materialOffsetComponentInstance: bottomPartMaterialOffsetInstance, materialOffsetOutput: bottomPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Bottom Part Material Offset",
            componentInstanceId: "bottom-part-material-offset",
            outputName: "Bottom Part Material Offset",
            material: bottomPartMaterial,
            width: width,
            length: length,
            heigth: sumSafeBottomHeightHalfPatternVerticalSize,
            edgeRadius: safeBottomRadius,
        })

    const bottomPartCut = createMattressCut(
        openingOnBottom ? "Border" : "Bottom Panel",
        [{curve: refCurve, side: "right"}],
        bottomPartFabricLayout,
        bottomPartMaterialOffset,
    )

    const {nodes: bottomPanelNodes} = getComponentGraphNodesForBottomPanel({
        defaultBottomPanelMaterialLegacyId: bottomPartDefaultMaterialLegacyId,
        mode: "mirror",
        width,
        length,
        topAndBottomPartMaterialOffsetComponentGraph,
    })
    const bottomPanelGroup = createMattressGroup("Bottom Panel", [...bottomPanelNodes, sumSafeBottomHeightHalfPatternVerticalSize])

    const bottomPartGroup = createMattressGroup("Bottom Part", [
        bottomPartMaterial,
        topAndBottomPartMaterialOffsetComponentGraph,
        bottomPartMaterialOffsetInstance,
        bottomPartMaterialOffset,
        bottomPartCut,
        bottomCurveAccessories,
        accessoriesGraph,
        bottomPanelGroup,
    ])

    // middle part / stack
    const {middlePartGroup, middlePartTopOffset, middlePartTopCurve} = getComponentGraphNodesForMiddlePart({
        bottomHeight: refCurveOffset,
        bottomHeightCurve: refCurve,
        layerReferenceFrameUnit: halfPatternVerticalSize,
        defaultMaterialLegacyId: middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        fabricLayout: middlePartFabricLayout,
    })

    // top part
    const sumSafeTopHeightHalfPatternVerticalSize = createMathOperator(
        "Sum Safe Top Height Half Pattern Vertical Size",
        "add",
        safeTopHeight,
        halfPatternVerticalSize,
    )
    const {topPartGroup} = getComponentGraphNodesForTopPart({
        topHeight: sumSafeTopHeightHalfPatternVerticalSize,
        width,
        length,
        safeTopRadius,
        middlePartTopCurve: middlePartTopCurve,
        defaultMaterialLegacyId: topPartDefaultMaterialLegacyId,
        fabricLayout: topPartFabricLayout,
        topAndBottomPartMaterialOffsetComponentGraph,
    })
    topPartGroup.parameters.nodes.addEntry(sumSafeTopHeightHalfPatternVerticalSize)

    const height = createMathOperator("Mattress Height", "add", middlePartTopOffset, safeTopHeight)
    const safeHeight = createMathOperator("Safe Height", "max", sumSafeTopRadiusSafeBottomRadius, height)
    const dimensionsGroup = createMattressGroup("Dimensions", [
        width,
        length,
        cornerRadius,
        topRadius,
        bottomRadius,
        bottomHeight,
        topHeight,
        patternVerticalSize,
        halfPatternVerticalSize,
        minAllowedBottomHeight,
        safeBottomHeight,
        minAllowedTopHeight,
        safeTopHeight,
        safeBottomRadius,
        safeTopRadius,
        sumSafeTopRadiusSafeBottomRadius,
        height,
        safeHeight,
    ])

    const mattressMesh = createMattressMesh("Mattress Mesh", {
        width,
        length,
        height: safeHeight,
        cornerRadius,
        topRadius: safeTopRadius,
        bottomRadius: safeBottomRadius,
    })

    return {
        mattressMesh,
        refCurveGroup,
        dimensionsGroup,
        topPartGroup,
        middlePartGroup,
        bottomPartGroup,
    }
}

// BS007A
export function initRootComponentGraphForStackableMattressStandardModelWithNonPlanarCurve_BS007A(
    options: InitStackableMattressStandardModelMasterTemplateOptions = {openingOnBottom: false},
    curveParams: {
        patternVerticalSize: number
        controlPointsData: Omit<MattressUVControlPoint, "uvSpace">[]
    },
) {
    const {
        openingOnBottom,
        topPartDefaultMaterialLegacyId,
        bottomPartDefaultMaterialLegacyId,
        middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        topPartFabricLayout,
        bottomPartFabricLayout,
        middlePartFabricLayout,
    } = options

    // dimensions
    const patternVerticalSize = createNumberInput(
        "Pattern Vertical Size",
        "pattern-vertical-size",
        curveParams.patternVerticalSize,
        numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 7),
    )
    const halfPatternVerticalSize = createMathOperator("Half Pattern Vertical Size", "mul", patternVerticalSize, 0.5)

    const width = createNumberInput("Width", "width", 90, numberInputMetaData([createPath(dimensionPathName, 0)], 50, 300, 0))
    const length = createNumberInput("Length", "length", 200, numberInputMetaData([createPath(dimensionPathName, 0)], 50, 300, 1))
    const cornerRadius = createNumberInput("Corner Radius", "corner-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 6))
    const topRadius = createNumberInput("Top Radius", "top-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 4))
    const bottomRadius = createNumberInput("Bottom Radius", "bottom-radius", 3, numberInputMetaData([createPath(dimensionPathName, 0)], 1, 20, 5))
    const bottomHeight = createNumberInput(
        "Bottom Height",
        "bottom-height",
        18,
        numberInputMetaData([createPath(dimensionPathName, 0), createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 1, 100, 3),
    )
    const topHeight = createNumberInput(
        "Top Height",
        "top-height",
        6,
        numberInputMetaData([createPath(dimensionPathName, 0), createPath(panelPathName, 0, createPath(topPanelPathName, 0))], 1, 100, 2),
    )
    const safeBottomRadius = createMathOperator("Safe Bottom Radius", "min", bottomRadius, cornerRadius)
    const safeTopRadius = createMathOperator("Safe Top Radius", "min", topRadius, cornerRadius)
    const sumSafeTopRadiusSafeBottomRadius = createMathOperator("Safe Top Radius + Safe Bottom Radius", "add", safeTopRadius, safeBottomRadius)

    const minAllowedBottomHeight = createMathOperator("Min Allowed Bottom Height", "add", patternVerticalSize, kEps)
    const safeBottomHeight = createMathOperator("Safe Bottom Height", "max", bottomHeight, minAllowedBottomHeight)

    // ref curve
    const refCurveOffsetNoEps = createMathOperator("Ref Curve Offset No Eps", "sub", safeBottomHeight, halfPatternVerticalSize)
    const refCurveOffset = createMathOperator("Ref Curve Offset", "add", refCurveOffsetNoEps, 0.001)
    const refReferenceFrame = createMattressReferenceFrame("Ref Reference Frame", "zAxisRing", "global", refCurveOffset, halfPatternVerticalSize)
    const refCurve = new MattressCurve({
        parameters: {
            name: "Ref Curve",
            closed: true,
            resolution: 1,
            controlPoints: curveParams.controlPointsData.map((pointData) => ({
                ...pointData,
                uvSpace: refReferenceFrame,
            })),
        },
    })
    const refCurveGroup = createMattressGroup("Ref Curve", [refReferenceFrame, refCurve, refCurveOffsetNoEps, refCurveOffset])

    // bottom part
    const {nodes: accessoriesNodes} = getComponentGraphForAccessoryStack(1)
    const accessoriesGraph = createMattressComponentGraph("Accessories", accessoriesNodes)
    const bottomCurveAccessories = createMattressComponentInstance("Accessories Bottom Curve", "accessories-bottom-curve", accessoriesGraph, {
        "ref-curve": refCurve,
    })
    const bottomPartMaterial = createMaterialInput(
        "Bottom Part Material",
        "bottom-part-material",
        uiPathMetaData([createPath(panelPathName, 0, createPath(bottomPanelPathName, 2))], 0),
        bottomPartDefaultMaterialLegacyId
            ? new MaterialReference({
                  parameters: {
                      name: "Default Bottom Part Material",
                      materialId: bottomPartDefaultMaterialLegacyId,
                      materialRevisionId: null,
                  },
              })
            : undefined,
    )
    const topAndBottomPartMaterialOffsetComponentGraph = getComponentGraphForTopAndBottomPartMaterialOffsetUtilityTemplate()
    const {materialOffsetComponentInstance: bottomPartMaterialOffsetInstance, materialOffsetOutput: bottomPartMaterialOffset} =
        getComponentInstanceAndOutputForTopAndBottomPartMaterialOffsetUtilityTemplate({
            componentGraph: topAndBottomPartMaterialOffsetComponentGraph,
            componentInstanceName: "Bottom Part Material Offset",
            componentInstanceId: "bottom-part-material-offset",
            outputName: "Bottom Part Material Offset",
            material: bottomPartMaterial,
            width: width,
            length: length,
            heigth: safeBottomHeight,
            edgeRadius: safeBottomRadius,
        })
    const bottomPartCut = createMattressCut(
        openingOnBottom ? "Border" : "Bottom Panel",
        [{curve: refCurve, side: "right"}],
        bottomPartFabricLayout,
        bottomPartMaterialOffset,
    )

    const {nodes: bottomPanelNodes} = getComponentGraphNodesForBottomPanel({
        defaultBottomPanelMaterialLegacyId: bottomPartDefaultMaterialLegacyId,
        mode: "mirror",
        width,
        length,
        topAndBottomPartMaterialOffsetComponentGraph,
    })
    const bottomPanelGroup = createMattressGroup("Bottom Panel", bottomPanelNodes)

    const bottomPartGroup = createMattressGroup("Bottom Part", [
        bottomPartMaterial,
        topAndBottomPartMaterialOffsetComponentGraph,
        bottomPartMaterialOffsetInstance,
        bottomPartMaterialOffset,
        bottomPartCut,
        bottomCurveAccessories,
        accessoriesGraph,
        bottomPanelGroup,
    ])

    // middle part / stack
    const {middlePartGroup, middlePartTopOffset, middlePartTopCurve} = getComponentGraphNodesForMiddlePart({
        bottomHeight: refCurveOffset,
        bottomHeightCurve: refCurve,
        layerReferenceFrameUnit: halfPatternVerticalSize,
        defaultMaterialLegacyId: middlePartDefaultMaterialLegacyId,
        defaultLayerHeight,
        defaultLayerCount,
        fabricLayout: middlePartFabricLayout,
    })

    // top part
    const sumTopHeightPatternVerticalSize = createMathOperator("Sum Top Height Pattern Vertical Size", "add", topHeight, patternVerticalSize)
    const {topPartGroup} = getComponentGraphNodesForTopPart({
        middlePartTopCurve: middlePartTopCurve,
        defaultMaterialLegacyId: topPartDefaultMaterialLegacyId,
        fabricLayout: topPartFabricLayout,
        topHeight: sumTopHeightPatternVerticalSize,
        width,
        length,
        safeTopRadius,
        topAndBottomPartMaterialOffsetComponentGraph,
    })
    topPartGroup.parameters.nodes.addEntry(sumTopHeightPatternVerticalSize)

    const middlePartHeight = createMathOperator("Middle Part Height", "add", middlePartTopOffset, halfPatternVerticalSize)
    const height = createMathOperator("Mattress Height", "add", middlePartHeight, topHeight)
    const safeHeight = createMathOperator("Safe Height", "max", sumSafeTopRadiusSafeBottomRadius, height)

    const dimensionsGroup = createMattressGroup("Dimensions", [
        patternVerticalSize,
        halfPatternVerticalSize,
        width,
        length,
        cornerRadius,
        topRadius,
        bottomRadius,
        bottomHeight,
        minAllowedBottomHeight,
        safeBottomHeight,
        topHeight,
        safeBottomRadius,
        safeTopRadius,
        sumSafeTopRadiusSafeBottomRadius,
        middlePartHeight,
        height,
        safeHeight,
    ])

    const mattressMesh = createMattressMesh("Mattress Mesh", {
        width,
        length,
        height: safeHeight,
        cornerRadius,
        topRadius: safeTopRadius,
        bottomRadius: safeBottomRadius,
    })

    return {
        mattressMesh,
        refCurveGroup,
        dimensionsGroup,
        topPartGroup,
        middlePartGroup,
        bottomPartGroup,
    }
}

export function initStackableMattressStandardModelMasterTemplate(
    modifyTemplateGraph: IModifyTemplateGraph,
    options: InitStackableMattressStandardModelMasterTemplateOptions,
) {
    const rootNodes = initRootComponentGraphForStackableMattressStandardModel(options)
    const mattress = createMattress([
        rootNodes.mattressMesh,
        rootNodes.dimensionsGroup,
        rootNodes.topPartGroup,
        rootNodes.middlePartGroup,
        rootNodes.bottomPartGroup,
        rootNodes.handlesGroup,
    ])
    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({list: [mattress]})
    })
}

// TODO: handles, pullers
export type InitMattressInstanceTemplateOptions = {
    parameters?: ParametersParameters
    defaultCamera?: Camera
    lightingTemplateLegacyId: number
    accessoriesTemplatesLegacyIds: {
        "piping 2mm": number
        "piping 5.5mm": number
        "zipper Reverse Coil, M3": number
        "zipper Reverse Coil, M5": number
        "zipper Spiral Coil, M3": number
        "zipper Spiral Coil, M5": number
        "zipper Teeth, M3": number
        "zipper Teeth, M5": number
        "tape 8mm": number
        "puller Round Big Hole Puller (r619) with Slider, M3": number
        "puller Round Big Hole Puller (r619) with Slider, M5": number
        "puller Square Big Hole Puller (r619) with Slider, M3": number
        "puller Square Big Hole Puller (r619) with Slider, M5": number
        "puller Square Puller Pointed (n51) with Slider, M3": number
        "puller Square Puller Pointed (n51) with Slider, M5": number
        "puller Standard Puller (YKK, n56) with Slider, M3": number
        "puller Standard Puller (YKK, n56) with Slider, M5": number
        "puller Square Puller with Slider, M3": number
        "vertical handle Default Vertical Handle": number
        "horizontal handle Default Horizontal Handle": number
        "horizontal handle Horizontal Handle, Cross Stitched": number
        "vertical handle Vertical Handle, 3.5cm": number
        "vertical handle Vertical Handle, 5.5cm": number
        "vertical handle Vertical Handle, 8cm": number
        "vertical handle Vertical Handle with 2mm Piping, 8cm": number
        "verticla handle Vertical Handle with Tape, 6cm": number
        "verticla handle Vertical Handle with Tape, 8cm": number
    }
}

export const getDefaultHandlesParameters = (options: InitMattressInstanceTemplateOptions) => {
    return {
        "front-side-vertical-handles-stack/spacing": 60,
        "back-side-vertical-handles-stack/spacing": 60,
        "front-side-horizontal-handles-stack/spacing": 60,
        "back-side-horizontal-handles-stack/spacing": 60,
        "left-side-vertical-handles-stack/spacing": 80,
        "right-side-vertical-handles-stack/spacing": 80,
        "left-side-horizontal-handles-stack/spacing": 80,
        "right-side-horizontal-handles-stack/spacing": 80,
        "right-side-vertical-handles-stack/handles-count": 2,
        "left-side-vertical-handles-stack/handles-count": 2,
        "right-side-horizontal-handles-stack/handles-count": 2,
        "left-side-horizontal-handles-stack/handles-count": 2,
    }
}

export function createLightingInstance(lightingTemplateLegacyId: number): TemplateInstance {
    return new TemplateInstance({
        parameters: {
            name: "Lighting",
            id: "lighting",
            template: createTemplateReference(lightingTemplateLegacyId),
            parameters: new Parameters({parameters: {}}),
            lockedTransform: Matrix4.identity().toArray(),
            visible: true,
        },
    })
}

export function insertLighting(modify: IModifyTemplateGraph, lightingTemplateLegacyId: number): void {
    modify.modifyTemplateGraph((templateGraph) => {
        templateGraph.parameters.nodes.addEntry(createLightingInstance(lightingTemplateLegacyId))
    })
}

export function initInstanceTemplateCommon(
    modifyTemplateGraph: IModifyTemplateGraph,
    masterTemplateData: {
        legacyId: number
        instanceName: string
        instanceId: string
    },
    options: InitMattressInstanceTemplateOptions,
) {
    const lightingInstance = createLightingInstance(options.lightingTemplateLegacyId)

    const masterTemplate = createTemplateReference(masterTemplateData.legacyId)

    const mattressInstance = new TemplateInstance({
        parameters: {
            name: masterTemplateData.instanceName,
            id: masterTemplateData.instanceId,
            template: masterTemplate,
            parameters: new Parameters({
                parameters: {
                    ...Object.fromEntries(Object.entries(options.parameters ?? {}).map(([key, value]) => [`mattress/${key}`, value])),
                },
            }),
            lockedTransform: Matrix4.identity().toArray(),
            visible: true,
        },
    })

    modifyTemplateGraph.modifyTemplateGraph((graph) => {
        graph.parameters.nodes.updateParameters({
            list: [lightingInstance, mattressInstance, options.defaultCamera].filter((node) => node !== undefined),
        })
    })
}
